import React, { useState, useEffect } from "react";

import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import DepartmentModal from "./AddDepartmentPopup";
import Table from "./Table";
import SuccessMessage from "../../Components/shared/SuccessMessage"; // Import the SuccessMessage component
import axiosInstance from "../../utils/axiosInstance";
import PageHeader from "../../Components/PageHeader";

function Departments() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // State for managing success/error messages
  const [successMessage, setSuccessMessage] = useState({
    show: false,
    message: "",
    isError: false,
  });

  // Fetch departments from the API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get("/departments");

        if (response.data.success && Array.isArray(response.data.departments)) {
          setDepartments(response.data.departments);
        } else {
          console.error(
            "Fetched data is not in the expected format:",
            response.data
          );
          setDepartments([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredDepartments = departments.filter(
    (department) =>
      (department.title?.toLowerCase() || "").includes(
        searchQuery.toLowerCase()
      ) ||
      (department.description?.toLowerCase() || "").includes(
        searchQuery.toLowerCase()
      )
  );

  // Pagination logic
  const indexOfLastDepartment = currentPage * itemsPerPage;
  const indexOfFirstDepartment = indexOfLastDepartment - itemsPerPage;
  const currentDepartments = filteredDepartments.slice(
    indexOfFirstDepartment,
    indexOfLastDepartment
  );

  const totalPages = Math.ceil(filteredDepartments.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleFormSubmit = async (e, selectedBranches = []) => {
    e.preventDefault();

    try {
      const url = editingDepartment
        ? `/departments/${editingDepartment.id}`
        : "/departments";
      const method = editingDepartment ? "put" : "post";

      // Add branchIds for hospital_admin
      const payload = {
        ...formData,
        ...(selectedBranches.length > 0 && { branchIds: selectedBranches }),
      };

      const response = await axiosInstance[method](url, payload);

      if (response.data.success) {
        if (editingDepartment) {
          setDepartments((prev) =>
            prev.map((dept) =>
              dept.id === editingDepartment.id
                ? {
                    ...dept,
                    title: formData.name,
                    description: formData.description,
                  }
                : dept
            )
          );
        } else {
          // Transform backend response to match frontend format (name -> title)
          const newDepartment = {
            id: response.data.department.id,
            title: response.data.department.name,
            description: response.data.department.description,
          };
          setDepartments((prev) => [...prev, newDepartment]);
        }

        // Show success message
        setSuccessMessage({
          show: true,
          message: editingDepartment
            ? "Department updated successfully!"
            : "Department added successfully!",
          isError: false,
        });

        // Reset form and close modal
        setIsModalOpen(false);
        setFormData({ name: "", description: "" });
        setEditingDepartment(null);
      } else {
        setSuccessMessage({
          show: true,
          message: "Failed to save department. Please try again.",
          isError: true,
        });
      }
    } catch (error) {
      console.error("Error saving department:", error);

      setSuccessMessage({
        show: true,
        message: "An error occurred. Please try again later.",
        isError: true,
      });
    }
  };

  const handleEdit = (department) => {
    setEditingDepartment(department);
    setFormData({
      name: department.title,
      description: department.description,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({ name: "", description: "" });
    setEditingDepartment(null);
  };

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this department?"
    );

    if (isConfirmed) {
      try {
        const response = await axiosInstance.delete(`/departments/${id}`);

        if (response.data.success) {
          setDepartments((prev) => prev.filter((dept) => dept.id !== id));

          setSuccessMessage({
            show: true,
            message: "Department deleted successfully!",
            isError: false,
          });
        } else {
          setSuccessMessage({
            show: true,
            message: "Failed to delete department. Please try again.",
            isError: true,
          });
        }
      } catch (error) {
        console.error("Error deleting department:", error);

        setSuccessMessage({
          show: true,
          message: "An error occurred. Please try again later.",
          isError: true,
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen justify-center items-center bg-gray-100">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page Header */}
      <PageHeader
        title="Departments"
        subtitle="Manage hospital departments and specialties"
        showBackButton={true}
        action={
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm"
          >
            Add Department
          </button>
        }
      />

      <div className="mb-4">
        <div className="relative w-full md:w-96">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search departments..."
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Table */}
      <Table
        data={currentDepartments.map((department, index) => ({
          ...department,
          index: index + 1 + (currentPage - 1) * itemsPerPage,
        }))}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Pagination Controls */}
      <div className="flex justify-center mt-4">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-4 py-2 border border-gray-300 rounded-md mr-2 bg-gray-200 disabled:bg-gray-300"
        >
          Prev
        </button>
        <span className="px-4 py-2">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-4 py-2 border border-gray-300 rounded-md ml-2 bg-gray-200 disabled:bg-gray-300"
        >
          Next
        </button>
      </div>

      {/* Success Message */}
      {successMessage.show && (
        <SuccessMessage
          message={successMessage.message}
          isError={successMessage.isError}
          onClose={() => setSuccessMessage({ ...successMessage, show: false })}
        />
      )}

      {/* Department Modal */}
      <DepartmentModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        handleCloseModal={handleCloseModal}
        handleFormSubmit={handleFormSubmit}
        formData={formData}
        handleInputChange={handleInputChange}
        title={editingDepartment ? "Edit Department" : "Add Department"}
        submitButtonText={editingDepartment ? "Save Changes" : "Add"}
      />
    </div>
  );
}

export default Departments;
